// frontend/src/views/SingleDocumentView.tsx

import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  Download,
  MessageCircle,
  Calendar,
  Tag,
  Eye,
  EyeOff,
  Maximize2,
} from "lucide-react";
import DocumentViewer from "../components/DocumentViewer";
import { FallbackPreview } from "../components/DocumentViewer";
import api, { getDocument, downloadDocument } from "../services/api";
import {
  getComments,
  createComment,
  createReply,
  type Comment,
} from "../services/comments";
import type { Document } from "../services/api";
import { CollapsibleCommentsPanel } from "../components/Comments";
import LoadingSpinner from "../components/ui/LoadingSpinner";

const SingleDocumentView: React.FC = () => {
  // Router hooks
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const returnTo = location.state?.returnTo || "/documents";

  // State hooks
  const [document, setDocument] = useState<Document | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState("");
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [commentsExpanded, setCommentsExpanded] = useState(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem(`medapp-comments-expanded-${id}`);
      return stored ? JSON.parse(stored) : false;
    }
    return false;
  });
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth < 768
  );
  const [isDownloading, setIsDownloading] = useState(false);

  // Callback hooks
  const handleViewerError = useCallback((error: Error) => {
    setError("Errore nel caricamento del documento");
  }, []);

  // Effect hooks
  useEffect(() => {
    if (typeof window !== "undefined" && id) {
      localStorage.setItem(
        `medapp-comments-expanded-${id}`,
        JSON.stringify(commentsExpanded)
      );
    }
  }, [commentsExpanded, id]);

  useEffect(() => {
    if (comments.length > 0 && id) {
      const hasStoredPreference = localStorage.getItem(
        `medapp-comments-expanded-${id}`
      );

      if (!hasStoredPreference && comments.length < 3) {
        setCommentsExpanded(true);
      }

      if (!hasStoredPreference && comments.length >= 5 && commentsExpanded) {
        setCommentsExpanded(false);
      }
    }
  }, [comments.length, id, commentsExpanded]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const documentId = parseInt(id || "0", 10);

        const docResponse = await getDocument(documentId);

        if (!docResponse.data.URL) {
          const API_BASE = api.defaults.baseURL ?? "/api";
          docResponse.data.URL = `${API_BASE}/documents/file/${docResponse.data.id}`;
        }
        setDocument(docResponse.data);

        const commentsResponse = await getComments([documentId]);
        setComments(commentsResponse.data);

        setError(null);
      } catch (err: any) {
        setError(
          err.response?.data?.error?.message || "Error loading document"
        );
        setDocument(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() === "c" && !event.ctrlKey && !event.metaKey) {
        const target = event.target as HTMLElement;
        if (target.tagName !== "INPUT" && target.tagName !== "TEXTAREA") {
          event.preventDefault();
          setCommentsExpanded((prev: boolean) => !prev);
        }
      }
    };

    window.document.addEventListener("keydown", handleKeyDown);
    return () => window.document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Handler functions
  const handleDownload = async () => {
    if (!document || isDownloading) return;

    try {
      setIsDownloading(true);
      const blob = await downloadDocument(document.id);
      const url = window.URL.createObjectURL(blob);
      const link = window.document.createElement("a");
      link.href = url;
      link.download = document.filename;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Errore durante il download del documento";
      setError(message);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleNewComment = async () => {
    if (!newComment.trim() || isSubmittingComment || !document) return;

    try {
      setIsSubmittingComment(true);
      await createComment(document.id, { content: newComment });
      const commentsResponse = await getComments([document.id]);
      setComments(commentsResponse.data);
      setNewComment("");
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Errore durante l'invio del commento";
      setError(message);
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const handleReplySubmit = async (content: string, parentId: number) => {
    if (!document) return;

    try {
      const response = await createReply(parentId, { content });
      if (response.success) {
        const commentsResponse = await getComments([document.id]);
        if (commentsResponse.success) {
          setComments(commentsResponse.data);
        }
      }
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Errore durante l'invio della risposta";
      setError(message);
    }
  };

  const toggleComments = () => {
    setCommentsExpanded((prev: boolean) => !prev);
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full mx-4">
          <LoadingSpinner message="Caricamento documento..." />
        </div>
      </div>
    );
  }

  // Error state
  if (error && !document) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Documento non trovato
          </h2>
          <p className="text-gray-600 mb-6">
            Il documento richiesto non è disponibile
          </p>
          <button
            onClick={() => navigate(returnTo)}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200"
          >
            Torna alla lista documenti
          </button>
        </div>
      </div>
    );
  }

  if (!document) return null;

  // Main render
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={() =>
                  navigate(returnTo, {
                    state: { activeTab: location.state?.activeTab },
                  })
                }
                className="p-2 hover:bg-gray-100 rounded-full transition-colors duration-200"
              >
                <ArrowLeft size={20} className="text-gray-600" />
              </button>

              <div className="flex-1">
                <h1 className="text-2xl font-bold text-gray-900 truncate">
                  {document.title || document.filename}
                </h1>
                {document.title && (
                  <p className="text-sm text-gray-500 mt-1 truncate">
                    {document.filename}
                  </p>
                )}
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex items-center space-x-3">
              <button
                onClick={toggleComments}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                  commentsExpanded
                    ? "bg-blue-100 text-blue-700 hover:bg-blue-200"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {commentsExpanded ? <EyeOff size={16} /> : <Eye size={16} />}
                <MessageCircle size={16} />
                <span className="hidden sm:inline">Commenti</span>
                {comments.length > 0 && (
                  <span className="bg-blue-600 text-white text-xs px-2 py-1 rounded-full">
                    {comments.length}
                  </span>
                )}
              </button>

              {user?.canDownload && (
                <button
                  onClick={handleDownload}
                  disabled={isDownloading}
                  className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200"
                >
                  <Download size={16} />
                  <span className="hidden sm:inline">
                    {isDownloading ? "Scaricando..." : "Download"}
                  </span>
                </button>
              )}
            </div>
          </div>

          {/* Document metadata */}
          <div className="flex flex-wrap items-center gap-4 pb-4">
            <div className="flex items-center text-gray-600">
              <Calendar size={16} className="mr-2" />
              <span className="text-sm">
                {new Date(document.exam_date).toLocaleDateString("it-IT", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </span>
            </div>

            {document.tags.length > 0 && (
              <div className="flex items-center space-x-2">
                <Tag size={16} className="text-gray-400" />
                <div className="flex flex-wrap gap-2">
                  {document.tags.map((tag: string) => (
                    <span
                      key={tag}
                      className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
          <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-lg">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm text-red-700">{error}</p>
              </div>
              <div className="ml-auto pl-3">
                <button
                  onClick={() => setError(null)}
                  className="text-red-400 hover:text-red-600 transition-colors duration-200"
                >
                  <svg
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-20">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Document Viewer */}
          <div
            className={`${commentsExpanded && !isMobile ? "lg:w-2/3" : "w-full"} transition-all duration-300`}
          >
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
              <div className="h-[70vh] flex flex-col">
                {document.URL || document.file_path ? (
                  <DocumentViewer
                    document={document}
                    showControls={true}
                    onError={handleViewerError}
                  />
                ) : (
                  <div className="flex-1 flex items-center justify-center bg-gray-50">
                    <FallbackPreview
                      type={document.type}
                      message="Documento non disponibile"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Comments Panel */}
          {commentsExpanded && (
            <div
              className={`${!isMobile ? "lg:w-1/3" : "w-full"} transition-all duration-300`}
            >
              <div className="bg-white rounded-xl shadow-lg border border-gray-200 flex flex-col">
                <CollapsibleCommentsPanel
                  expanded={true}
                  onToggle={() => {}} // Managed by header button
                  comments={comments}
                  onNewComment={handleNewComment}
                  newComment={newComment}
                  onNewCommentChange={setNewComment}
                  isSubmittingComment={isSubmittingComment}
                  onReplySubmit={handleReplySubmit}
                  onDownload={undefined}
                  canDownload={false}
                  className="flex-1 flex flex-col min-h-[70vh]"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Comments Panel */}
      {commentsExpanded && isMobile && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end">
          <div className="bg-white w-full max-h-[80vh] rounded-t-2xl overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                Commenti ({comments.length})
              </h3>
              <button
                onClick={toggleComments}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <CollapsibleCommentsPanel
              expanded={true}
              onToggle={() => {}}
              comments={comments}
              onNewComment={handleNewComment}
              newComment={newComment}
              onNewCommentChange={setNewComment}
              isSubmittingComment={isSubmittingComment}
              onReplySubmit={handleReplySubmit}
              onDownload={undefined}
              canDownload={false}
              className="flex-1 max-h-[calc(80vh-5rem)]"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default SingleDocumentView;
